import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { collection, doc, getDocs, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useLocationSharing } from "../hooks/useLocationSharing";
import { Sheet } from "../components/Sheet";
import { AppFooter } from "../components/AppFooter";
import { PatchNoteList } from "../components/PatchNoteList";
import { ACCENT_SCHEMES, loadAccent, setAccent, type AccentScheme } from "../lib/accentColor";
import { PATCH_NOTES } from "../lib/patchNotes";

const SUBCOLLECTIONS = [
  "calendarEvents",
  "eventCategories",
  "shoppingItems",
  "lists",
  "linkBoards",
  "savingsGoals",
  "todos",
  "importantDates",
  "importantDateCategories",
  "notes",
  "moods",
  "locations",
];

// Both "lists" and "linkBoards" have a nested subcollection per document
// (items/ and links/) that has to be cleared before the parent doc is
// deleted below — otherwise those nested documents would be orphaned.
const NESTED_SUBCOLLECTIONS: Record<string, string> = {
  lists: "items",
  linkBoards: "links",
};

async function deleteCoupleData(coupleId: string) {
  for (const [parent, nested] of Object.entries(NESTED_SUBCOLLECTIONS)) {
    const parentSnap = await getDocs(collection(db, "couples", coupleId, parent));
    for (const parentDoc of parentSnap.docs) {
      const nestedSnap = await getDocs(collection(db, "couples", coupleId, parent, parentDoc.id, nested));
      for (let i = 0; i < nestedSnap.docs.length; i += 450) {
        const batch = writeBatch(db);
        for (const d of nestedSnap.docs.slice(i, i + 450)) batch.delete(d.ref);
        await batch.commit();
      }
    }
  }
  for (const sub of SUBCOLLECTIONS) {
    const snap = await getDocs(collection(db, "couples", coupleId, sub));
    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += 450) {
      const batch = writeBatch(db);
      for (const d of docs.slice(i, i + 450)) batch.delete(d.ref);
      await batch.commit();
    }
  }
  const finalBatch = writeBatch(db);
  finalBatch.delete(doc(db, "invites", coupleId));
  finalBatch.delete(doc(db, "couples", coupleId));
  await finalBatch.commit();
}

function friendlyDeleteError(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Falsches Passwort.";
      case "auth/too-many-requests":
        return "Zu viele Versuche. Bitte kurz warten und erneut probieren.";
      default:
        return "Konto konnte nicht gelöscht werden. Bitte erneut versuchen.";
    }
  }
  return "Konto konnte nicht gelöscht werden. Bitte erneut versuchen.";
}

export function Account() {
  const { user, deleteAccount } = useAuth();
  const { profile, couple, partner } = useCouple();
  const navigate = useNavigate();
  const [unpairing, setUnpairing] = useState(false);
  const [unpairError, setUnpairError] = useState<string | null>(null);
  // 0 = closed, 1 = first confirmation, 2 = second, stronger confirmation —
  // this is the one action in the app that destroys the partner's data too,
  // so a single tap (or a tap fat-fingered on a phone) must never be enough.
  const [unpairStep, setUnpairStep] = useState<0 | 1 | 2>(0);

  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);

  // Per-device preference, not couple data — each partner can pick their
  // own without affecting the other (see src/lib/accentColor.ts).
  const [accent, setAccentState] = useState<AccentScheme>(() => loadAccent());
  function chooseAccent(scheme: AccentScheme) {
    setAccent(scheme);
    setAccentState(scheme);
  }

  // "Zusammen seit" — draft state so typing in the date picker doesn't write
  // on every keystroke; synced from the live couple doc whenever it's not
  // actively being edited, same idea as TodoCommentsSheet's draft pattern.
  const [togetherSinceDraft, setTogetherSinceDraft] = useState("");
  const [togetherSinceEditing, setTogetherSinceEditing] = useState(false);
  const [togetherSinceSaving, setTogetherSinceSaving] = useState(false);
  const togetherSinceValue = togetherSinceEditing ? togetherSinceDraft : (couple?.togetherSince ?? "");

  async function saveTogetherSince(value: string) {
    if (!couple) return;
    setTogetherSinceSaving(true);
    try {
      await updateDoc(doc(db, "couples", couple.id), { togetherSince: value || null });
    } finally {
      setTogetherSinceSaving(false);
      setTogetherSinceEditing(false);
    }
  }

  const location = useLocationSharing();

  async function confirmUnpair() {
    if (!couple) return;
    setUnpairStep(0);
    setUnpairing(true);
    setUnpairError(null);
    try {
      await deleteCoupleData(couple.id);
    } catch {
      setUnpairError("Entpaaren hat nicht vollständig geklappt. Bitte erneut versuchen.");
    } finally {
      setUnpairing(false);
    }
  }

  async function handleDeleteAccount(e: FormEvent) {
    e.preventDefault();
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteAccount(password);
      navigate("/login");
    } catch (err) {
      setDeleteError(friendlyDeleteError(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Konto</h1>

      <div className="card mt-5 rounded-2xl p-4">
        <p className="text-sm text-ink-400 dark:text-night-faint">Angemeldet als</p>
        <p className="mt-0.5 font-semibold text-ink-900 dark:text-night-text">{profile?.displayName} · @{profile?.username}</p>
        <p className="text-sm text-ink-600 dark:text-night-muted">{user?.email}</p>
      </div>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Farbschema</h2>
        <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">Nur auf diesem Gerät — jede:r kann eine eigene Farbe wählen.</p>
        <div className="card mt-2 flex flex-wrap gap-3 rounded-2xl p-4">
          {ACCENT_SCHEMES.map((s) => (
            <button
              key={s.id}
              onClick={() => chooseAccent(s.id)}
              aria-pressed={accent === s.id}
              className="flex flex-col items-center gap-1.5"
            >
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full"
                style={{ backgroundColor: s.swatch, boxShadow: accent === s.id ? `0 0 0 3px ${s.swatch}99` : "none" }}
              >
                {accent === s.id && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#fdf6f3" strokeWidth="2.4" className="h-4.5 w-4.5">
                    <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-xs text-ink-600 dark:text-night-muted">{s.label}</span>
            </button>
          ))}
        </div>
      </section>

      {couple && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Verbindung</h2>
          <div className="card mt-2 rounded-2xl p-4">
            {couple.partnerLeftUid ? (
              // The other partner deleted their account (see leaveCouple) —
              // memberUids now only has this uid. Everything shared stays
              // fully intact and accessible; "Verbindung auflösen" below
              // still works exactly as before if a fresh start is wanted.
              <p className="text-sm text-ink-600 dark:text-night-muted">
                Dein/e Partner/in hat den Account gelöscht. Eure gemeinsamen Inhalte sind weiterhin hier — löse die
                Verbindung auf, wenn du neu starten möchtest.
              </p>
            ) : (
              <p className="text-sm text-ink-600 dark:text-night-muted">
                Verbunden mit <span className="font-semibold text-ink-900 dark:text-night-text">{partner?.displayName ?? "…"}</span>
              </p>
            )}
            <button
              onClick={() => setUnpairStep(1)}
              disabled={unpairing}
              className="mt-3 h-11 w-full rounded-xl border border-garnet-600 text-sm font-semibold text-garnet-600 disabled:opacity-50"
            >
              {unpairing ? "Wird aufgelöst…" : "Verbindung auflösen"}
            </button>
            {unpairError && <p className="mt-2 text-sm text-garnet-600">{unpairError}</p>}
          </div>
        </section>
      )}

      {couple && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Zusammen seit</h2>
          <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
            Optional — zeigt auf der Startseite dezent an, seit wie vielen Tagen ihr zusammen seid.
          </p>
          <div className="card mt-2 flex items-center gap-3 rounded-2xl p-4">
            <input
              type="date"
              value={togetherSinceValue}
              onFocus={() => {
                setTogetherSinceDraft(couple.togetherSince ?? "");
                setTogetherSinceEditing(true);
              }}
              onChange={(e) => setTogetherSinceDraft(e.target.value)}
              onBlur={(e) => saveTogetherSince(e.target.value)}
              className="h-11 flex-1 rounded-lg border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
            {couple.togetherSince && (
              <button
                onClick={() => saveTogetherSince("")}
                disabled={togetherSinceSaving}
                aria-label="Entfernen"
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-cream-200 text-ink-400 disabled:opacity-50 dark:border-night-border dark:text-night-faint"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                  <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        </section>
      )}

      {couple && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Standort teilen</h2>
          <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
            Optional — {partner?.displayName ?? "dein Partner/deine Partnerin"} sieht nur die Entfernung in km, nie deinen
            genauen Standort oder Ort. Nur für dieses Gerät.
          </p>
          <div className="card mt-2 rounded-2xl p-4">
            {!location.supported ? (
              <p className="text-sm text-ink-600 dark:text-night-muted">Dieser Browser unterstützt keine Standortfreigabe.</p>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-ink-900 dark:text-night-text">{location.enabled ? "Aktiviert" : "Nicht aktiviert"}</p>
                  <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">
                    {location.enabled ? "Dieses Gerät teilt aktuell deinen Standort." : "Für dieses Gerät noch nicht aktiviert."}
                  </p>
                </div>
                <button
                  onClick={location.enabled ? location.disable : location.enable}
                  disabled={location.busy}
                  className={`h-10 flex-shrink-0 rounded-xl px-4 text-sm font-semibold disabled:opacity-50 ${
                    location.enabled ? "border border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text" : "bg-plum-700 text-cream-50"
                  }`}
                >
                  {location.busy ? "…" : location.enabled ? "Deaktivieren" : "Aktivieren"}
                </button>
              </div>
            )}
            {location.error && <p className="mt-3 text-sm text-garnet-600">{location.error}</p>}
            {location.enabled && !location.partnerEnabled && (
              <p className="mt-3 text-sm text-ink-400 dark:text-night-faint">
                Die Entfernung erscheint erst, sobald {partner?.displayName ?? "dein Partner/deine Partnerin"} es auch
                aktiviert hat.
              </p>
            )}
          </div>
        </section>
      )}

      <Sheet open={unpairStep === 1} onClose={() => setUnpairStep(0)} title="Verbindung auflösen?">
        <p className="text-sm text-ink-600 dark:text-night-muted">
          Kalender, Einkaufsliste, Sparziele, Todos, wichtige Daten, Notizen und Stimmungsverlauf werden für euch beide
          unwiderruflich gelöscht.
        </p>
        <div className="mt-5 flex gap-3">
          <button onClick={() => setUnpairStep(0)} className="h-11 flex-1 rounded-xl border border-cream-200 text-sm font-semibold text-ink-900 dark:border-night-border dark:text-night-text">
            Abbrechen
          </button>
          <button onClick={() => setUnpairStep(2)} className="h-11 flex-1 rounded-xl bg-garnet-600 text-sm font-semibold text-cream-50">
            Weiter
          </button>
        </div>
      </Sheet>

      <Sheet open={unpairStep === 2} onClose={() => setUnpairStep(0)} title="Bist du dir wirklich sicher?">
        <p className="text-sm text-ink-600 dark:text-night-muted">
          Das kann nicht rückgängig gemacht werden. {partner?.displayName ?? "Dein Partner/deine Partnerin"} verliert die
          gemeinsamen Daten ebenfalls, ohne vorher gefragt zu werden.
        </p>
        <div className="mt-5 flex gap-3">
          <button onClick={() => setUnpairStep(0)} className="h-11 flex-1 rounded-xl border border-cream-200 text-sm font-semibold text-ink-900 dark:border-night-border dark:text-night-text">
            Abbrechen
          </button>
          <button onClick={confirmUnpair} className="h-11 flex-1 rounded-xl bg-garnet-600 text-sm font-semibold text-cream-50">
            Ja, endgültig auflösen
          </button>
        </div>
      </Sheet>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-garnet-600">Konto löschen</h2>
        <div className="card mt-2 rounded-2xl border border-garnet-100 p-4 dark:border-garnet-600/25">
          {couple ? (
            <p className="text-sm text-ink-600 dark:text-night-muted">
              Ihr seid noch verbunden. Löse zuerst oben die Verbindung auf, bevor du dein Konto löschst — so bleiben eure
              gemeinsamen Daten nicht an einem gelöschten Konto hängen.
            </p>
          ) : (
            <>
              <p className="text-sm text-ink-600 dark:text-night-muted">
                Dein Konto und dein Login werden dauerhaft gelöscht. Das kann nicht rückgängig gemacht werden.
              </p>
              <form onSubmit={handleDeleteAccount} className="mt-4 space-y-3">
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="Passwort zur Bestätigung"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-lg border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-garnet-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
                />
                <label className="flex items-start gap-2.5 text-sm text-ink-900 dark:text-night-text">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                  />
                  Mir ist bewusst, dass diese Aktion endgültig ist.
                </label>
                {deleteError && <p className="text-sm text-garnet-600">{deleteError}</p>}
                <button
                  type="submit"
                  disabled={deleting || !confirmChecked}
                  className="h-11 w-full rounded-xl bg-garnet-600 text-sm font-semibold text-cream-50 disabled:opacity-50"
                >
                  {deleting ? "Wird gelöscht…" : "Konto endgültig löschen"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Versionshistorie</h2>
        <button
          onClick={() => setHistoryOpen(true)}
          className="card card-interactive mt-2 flex w-full items-center justify-between rounded-2xl p-4 text-left"
        >
          <div>
            <p className="text-sm font-medium text-ink-900 dark:text-night-text">Was ist neu?</p>
            <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">Alle bisherigen Änderungen an Wir, chronologisch.</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 flex-shrink-0 text-ink-400 dark:text-night-faint">
            <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </section>

      <Sheet open={historyOpen} onClose={() => setHistoryOpen(false)} title="Versionshistorie">
        <PatchNoteList notes={PATCH_NOTES} />
      </Sheet>

      <AppFooter />
    </main>
  );
}
